import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Plus,
  Users2,
  Trash2,
  Save,
  Loader2,
  Search,
  LayoutGrid,
  List as ListIcon,
  Mail,
  UserPlus,
  X,
  Building2,
  Pencil,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { api, formatApiErrorDetail } from "../../lib/api";
import { teamsApi } from "../../lib/copApi";
import { contactsApi } from "../../lib/contactsApi";
import { useAuthStore } from "../../store/authStore";

const DEFAULT_COLORS = [
  "#6366f1",
  "#059669",
  "#f59e0b",
  "#8b5cf6",
  "#0ea5e9",
  "#dc2626",
  "#14b8a6",
  "#ec4899",
];

// ------- helpers --------------------------------------------------
function initials(name = "") {
  return (name || "?")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0])
    .join("")
    .toUpperCase();
}

function AvatarStack({ users, memberIds, size = "sm" }) {
  const members = memberIds
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean);
  const cap = size === "lg" ? 8 : 5;
  const shown = members.slice(0, cap);
  const rest = Math.max(0, members.length - shown.length);
  if (members.length === 0)
    return <span className="text-xs text-zinc-400">Henüz üye yok</span>;
  const cls =
    size === "lg" ? "h-9 w-9 text-xs" : "h-7 w-7 text-[10px]";
  return (
    <div className="flex -space-x-2">
      {shown.map((m) => (
        <div
          key={m.id}
          title={m.name}
          className={`${cls} flex items-center justify-center rounded-full border-2 border-white bg-gradient-to-br from-violet-500 to-fuchsia-500 font-semibold text-white shadow`}
        >
          {initials(m.name)}
        </div>
      ))}
      {rest > 0 && (
        <div className={`${cls} flex items-center justify-center rounded-full border-2 border-white bg-zinc-200 font-semibold text-zinc-700`}>
          +{rest}
        </div>
      )}
    </div>
  );
}

// ------- Create/Edit Dialog --------------------------------------
function TeamDialog({ open, onOpenChange, users, initial, onSaved }) {
  const isEdit = !!initial?.id;
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [color, setColor] = useState(initial?.color || DEFAULT_COLORS[0]);
  const [memberIds, setMemberIds] = useState(initial?.member_ids || []);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (open) {
      setName(initial?.name || "");
      setDescription(initial?.description || "");
      setColor(initial?.color || DEFAULT_COLORS[0]);
      setMemberIds(initial?.member_ids || []);
      setQ("");
    }
  }, [open, initial]);

  const toggle = (id) =>
    setMemberIds((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );

  const filtered = useMemo(() => {
    const rx = q.trim().toLowerCase();
    return users.filter(
      (u) =>
        !rx ||
        (u.name || "").toLowerCase().includes(rx) ||
        (u.email || "").toLowerCase().includes(rx),
    );
  }, [users, q]);

  const save = async () => {
    if (!name.trim()) return;
    setBusy(true);
    try {
      const payload = { name: name.trim(), description, color, member_ids: memberIds };
      const doc = isEdit
        ? await teamsApi.update(initial.id, payload)
        : await teamsApi.create(payload);
      toast.success(isEdit ? "Takım güncellendi" : "Takım oluşturuldu");
      onSaved(doc);
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="team-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Takımı düzenle" : "Yeni takım"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Ad</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
              data-testid="team-name-input"
            />
          </div>
          <div>
            <Label>Açıklama</Label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div>
            <Label>Renk</Label>
            <div className="mt-1 flex flex-wrap gap-2">
              {DEFAULT_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={
                    "h-7 w-7 rounded-full border-2 transition-transform " +
                    (color === c ? "scale-110 border-zinc-900" : "border-white")
                  }
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="mb-1 flex items-center justify-between">
              <Label>Üyeler ({memberIds.length})</Label>
              <div className="relative w-40">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Ara"
                  className="h-8 pl-7 text-xs"
                />
              </div>
            </div>
            <div className="max-h-60 space-y-1 overflow-y-auto rounded-md border border-zinc-200 p-2">
              {filtered.map((u) => {
                const on = memberIds.includes(u.id);
                return (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => toggle(u.id)}
                    className={
                      "flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors " +
                      (on ? "bg-violet-50 text-violet-900" : "hover:bg-zinc-50")
                    }
                  >
                    <input type="checkbox" checked={on} readOnly className="h-3.5 w-3.5" />
                    <span className="flex-1 truncate">{u.name}</span>
                    <span className="text-[11px] text-zinc-400">{u.email}</span>
                  </button>
                );
              })}
              {filtered.length === 0 && (
                <div className="py-4 text-center text-xs text-zinc-400">Kullanıcı yok</div>
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            İptal
          </Button>
          <Button
            onClick={save}
            disabled={!name.trim() || busy}
            className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
            data-testid="team-save-btn"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------- Detail Drawer (Slack-style side sheet) -------------------
function TeamDetailDrawer({ open, onOpenChange, team, users, companies, canManage, onEdit, onDelete, onMembersChanged, onUserChanged }) {
  const [addingId, setAddingId] = useState("");
  const [managingMember, setManagingMember] = useState(null); // user object whose companies we're editing
  const [editingCompany, setEditingCompany] = useState(null); // company object whose owners we're editing
  const [managedQuery, setManagedQuery] = useState("");
  const [memberQuery, setMemberQuery] = useState("");
  const [ownerFilter, setOwnerFilter] = useState("__all__");
  if (!team) return null;

  const members = (team.member_ids || [])
    .map((id) => users.find((u) => u.id === id))
    .filter(Boolean);
  const nonMembers = users.filter((u) => !team.member_ids?.includes(u.id));

  // Team's managed companies = UNION of every member's `assigned_company_ids`.
  // Derived, not stored — reflects assignment changes instantly without a
  // team-level write path.
  const managedCompanyIds = Array.from(
    new Set(members.flatMap((m) => m.assigned_company_ids || [])),
  );
  const managedCompanies = managedCompanyIds
    .map((id) => (companies || []).find((c) => c.id === id))
    .filter(Boolean);
  // Two-stage filter: text search + owner select. Both filters compose so a
  // user can pick "Ayşe Yılmaz" then type "Samsung" to narrow further.
  const filteredManagedCompanies = managedCompanies.filter((c) => {
    if (ownerFilter !== "__all__") {
      const ownerHas = members.some(
        (m) => m.id === ownerFilter && (m.assigned_company_ids || []).includes(c.id),
      );
      if (!ownerHas) return false;
    }
    if (managedQuery.trim()) {
      if (!(c.name || "").toLowerCase().includes(managedQuery.trim().toLowerCase())) {
        return false;
      }
    }
    return true;
  });
  // Members list filter — search across name + email.
  const filteredMembers = memberQuery.trim()
    ? members.filter((m) => {
        const needle = memberQuery.trim().toLowerCase();
        return (
          (m.name || "").toLowerCase().includes(needle) ||
          (m.email || "").toLowerCase().includes(needle)
        );
      })
    : members;

  const addMember = async (uid) => {
    try {
      const doc = await teamsApi.addMember(team.id, uid);
      toast.success("Üye eklendi");
      onMembersChanged(doc);
      setAddingId("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const removeMember = async (uid) => {
    try {
      const doc = await teamsApi.removeMember(team.id, uid);
      toast.success("Üye çıkarıldı");
      onMembersChanged(doc);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <>
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-lg" data-testid="team-detail-drawer">
        <SheetHeader className="mb-4">
          <div className="flex items-start gap-3">
            <div
              className="flex h-11 w-11 flex-none items-center justify-center rounded-xl text-white shadow"
              style={{ backgroundColor: team.color || "#6366f1" }}
            >
              <Users2 className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <SheetTitle className="text-left text-lg" data-testid="team-detail-title">
                {team.name}
              </SheetTitle>
              <div className="mt-0.5 text-[11px] text-zinc-500">
                {members.length} üye
              </div>
              {team.description && (
                <p className="mt-2 text-xs text-zinc-600">{team.description}</p>
              )}
            </div>
          </div>
        </SheetHeader>

        {/* Add member dropdown */}
        {canManage && nonMembers.length > 0 && (
          <div className="mb-3 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 p-2.5">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              Üye ekle
            </div>
            <div className="flex items-center gap-2">
              <select
                value={addingId}
                onChange={(e) => setAddingId(e.target.value)}
                className="h-8 flex-1 rounded-md border border-zinc-200 bg-white px-2 text-xs"
              >
                <option value="">— Kullanıcı seç —</option>
                {nonMembers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={!addingId}
                onClick={() => addingId && addMember(addingId)}
                className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="team-add-member-btn"
              >
                <UserPlus className="h-3.5 w-3.5" />
                Ekle
              </Button>
            </div>
          </div>
        )}

        <div className="mb-2 flex items-center justify-between gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          <span>Üyeler ({members.length})</span>
        </div>
        {members.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-6 text-center text-sm text-zinc-500">
            Henüz üye yok.
          </div>
        ) : (
          <>
            {members.length > 3 && (
              <div className="relative mb-2">
                <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                <Input
                  value={memberQuery}
                  onChange={(e) => setMemberQuery(e.target.value)}
                  placeholder="Üye ara…"
                  className="h-8 pl-7 text-xs"
                  data-testid="team-member-search"
                />
                {memberQuery && (
                  <button
                    type="button"
                    onClick={() => setMemberQuery("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                    data-testid="team-member-search-clear"
                    title="Temizle"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </div>
            )}
            {filteredMembers.length === 0 ? (
              <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-3 text-center text-[11px] text-zinc-500">
                &quot;{memberQuery}&quot; için üye bulunamadı.
              </div>
            ) : (
          <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
            {filteredMembers.map((m) => {
              const count = (m.assigned_company_ids || []).length;
              return (
                <li
                  key={m.id}
                  className="group flex items-center gap-3 px-3 py-2.5"
                  data-testid={`team-member-${m.id}`}
                >
                  <div className="flex h-9 w-9 flex-none items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-xs font-semibold text-white">
                    {initials(m.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-zinc-900">{m.name}</div>
                    <div className="flex items-center gap-1 truncate text-[11px] text-zinc-500">
                      <Mail className="h-3 w-3" />
                      {m.email}
                    </div>
                  </div>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => setManagingMember(m)}
                      className={
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium transition " +
                        (count > 0
                          ? "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"
                          : "border-dashed border-zinc-300 bg-zinc-50 text-zinc-500 hover:border-zinc-400 hover:text-zinc-700")
                      }
                      title="Bu kişinin yönettiği şirketleri düzenle"
                      data-testid={`member-manage-companies-${m.id}`}
                    >
                      <Building2 className="h-3 w-3" />
                      {count > 0 ? `${count} şirket` : "Şirket ata"}
                    </button>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => removeMember(m.id)}
                      className="rounded p-1 text-zinc-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100"
                      title="Üyeyi çıkar"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
            )}
          </>
        )}

        {/* --- Yönetilen Şirketler (derived) --------------------------
            Bu takıma değil, üyelerine atanmış şirketlerin birleşimi.
            Bir üyenin atamaları değişince buradaki liste anında güncellenir
            (managedCompanyIds render zamanında hesaplanıyor). */}
        <div className="mt-5 mb-2 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          <span>Yönetilen Şirketler ({managedCompanies.length})</span>
          <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[9px] font-medium normal-case tracking-normal text-zinc-500">
            üyelerden türetildi
          </span>
        </div>
        {managedCompanies.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
            Üyelere şirket ataması yapıldığında burada listelenecek.
          </div>
        ) : (
          <>
            {(managedCompanies.length > 3 || members.length > 1) && (
              <div className="mb-2 flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
                  <Input
                    value={managedQuery}
                    onChange={(e) => setManagedQuery(e.target.value)}
                    placeholder="Şirket ara…"
                    className="h-8 pl-7 text-xs"
                    data-testid="team-managed-search"
                  />
                  {managedQuery && (
                    <button
                      type="button"
                      onClick={() => setManagedQuery("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600"
                      data-testid="team-managed-search-clear"
                      title="Temizle"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  )}
                </div>
                {members.length > 1 && (
                  <select
                    value={ownerFilter}
                    onChange={(e) => setOwnerFilter(e.target.value)}
                    className="h-8 max-w-[45%] rounded-md border border-zinc-200 bg-white px-2 text-xs"
                    data-testid="team-owner-filter"
                    title="Kullanıcıya göre süz"
                  >
                    <option value="__all__">Tüm sorumlular</option>
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}
            {filteredManagedCompanies.length === 0 ? (
              <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-3 text-center text-[11px] text-zinc-500">
                {ownerFilter !== "__all__" || managedQuery.trim()
                  ? "Bu filtre için sonuç bulunamadı."
                  : "Sonuç yok."}
              </div>
            ) : (
              <ul className="divide-y divide-zinc-100 rounded-lg border border-zinc-200 bg-white">
                {filteredManagedCompanies.map((c) => {
                  const owners = members.filter((m) =>
                    (m.assigned_company_ids || []).includes(c.id),
                  );
                  return (
                    <li
                      key={c.id}
                      className="group flex items-center gap-3 px-3 py-2.5"
                      data-testid={`team-company-${c.id}`}
                    >
                      <div className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
                        <Building2 className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-zinc-900">{c.name}</div>
                        <div className="truncate text-[11px] text-zinc-500">
                          {owners.map((o) => o.name).join(" · ") || "—"}
                        </div>
                      </div>
                      {canManage && (
                        <button
                          type="button"
                          onClick={() => setEditingCompany(c)}
                          className="rounded p-1 text-zinc-400 opacity-0 transition-opacity hover:bg-zinc-100 hover:text-zinc-700 group-hover:opacity-100"
                          title="Sorumluları düzenle"
                          data-testid={`edit-company-owners-${c.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}


        <div className="sticky bottom-0 mt-5 flex items-center justify-between gap-2 border-t border-zinc-200 bg-white pt-3">
          {canManage ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(team)}
              className="gap-1 text-red-600 hover:bg-red-50 hover:text-red-700"
              data-testid="team-delete-btn"
            >
              <Trash2 className="h-4 w-4" />
              Takımı sil
            </Button>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => onOpenChange(false)}>
              Kapat
            </Button>
            {canManage && (
              <Button
                size="sm"
                onClick={() => onEdit(team)}
                className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
              >
                Düzenle
              </Button>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
    <MemberCompaniesDialog
      open={!!managingMember}
      onOpenChange={(v) => !v && setManagingMember(null)}
      member={managingMember}
      companies={companies}
      onSaved={(updatedUser) => {
        onUserChanged?.(updatedUser);
        setManagingMember(null);
      }}
    />
    <CompanyOwnersDialog
      open={!!editingCompany}
      onOpenChange={(v) => !v && setEditingCompany(null)}
      company={editingCompany}
      // Show ALL tenant users here, not just the current team's members —
      // a company can be owned by anyone regardless of team membership,
      // and users may need to be removed from other teams' assignments too.
      users={users}
      teams={/* teams prop not passed to drawer; skip team badges here */ []}
      onSaved={(updatedUsers) => {
        // updatedUsers is an array of the freshly-PATCHed User docs.
        updatedUsers.forEach((u) => onUserChanged?.(u));
      }}
    />
    </>
  );
}

// ------- Member ↔ Company assignment mini modal ---------------------
// Opens from a team-drawer member row. Toggles company ids in and out of
// the user's `assigned_company_ids` via a single PATCH. The team drawer's
// "Yönetilen Şirketler" list is derived, so a save here refreshes both
// the member badge and the team's aggregate list on the next render.
function MemberCompaniesDialog({ open, onOpenChange, member, companies, onSaved }) {
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    if (member) {
      setSelected(member.assigned_company_ids || []);
      setQ("");
    }
  }, [member?.id]);

  if (!member) return null;

  const filtered = (companies || []).filter((c) => {
    if (!q.trim()) return true;
    return (c.name || "").toLowerCase().includes(q.trim().toLowerCase());
  });

  const toggle = (id) =>
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id],
    );

  const save = async () => {
    setSaving(true);
    try {
      const updated = await api
        .patch(`/users/${member.id}`, { assigned_company_ids: selected })
        .then((r) => r.data);
      toast.success("Şirket atamaları güncellendi");
      onSaved?.(updated);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="member-companies-dialog">
        <DialogHeader>
          <DialogTitle>{member.name} — Yönetilen Şirketler</DialogTitle>
          <div className="text-xs text-zinc-500">
            Bu kullanıcının sorumlu olduğu şirketleri işaretle. Ait olduğu
            takım(lar)ın &quot;Yönetilen Şirketler&quot; listesi buna göre
            otomatik güncellenir.
          </div>
        </DialogHeader>
        <div className="mb-2">
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Şirket ara…"
            data-testid="member-companies-search"
          />
        </div>
        <div className="max-h-[45vh] overflow-y-auto rounded-md border border-zinc-200 bg-white">
          {filtered.length === 0 ? (
            <div className="p-4 text-center text-xs text-zinc-500">
              Şirket bulunamadı.
            </div>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {filtered.map((c) => {
                const checked = selected.includes(c.id);
                return (
                  <li key={c.id}>
                    <label
                      className="flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-zinc-50"
                      data-testid={`member-company-row-${c.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(c.id)}
                        className="h-4 w-4 rounded border-zinc-300 text-violet-600 focus:ring-violet-500"
                        data-testid={`member-company-toggle-${c.id}`}
                      />
                      <div className="flex h-7 w-7 flex-none items-center justify-center rounded-md bg-gradient-to-br from-sky-500 to-cyan-500 text-white">
                        <Building2 className="h-3.5 w-3.5" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium text-zinc-900">{c.name}</div>
                        {c.industry && (
                          <div className="truncate text-[11px] text-zinc-500">{c.industry}</div>
                        )}
                      </div>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div className="text-[11px] text-zinc-500">
          {selected.length} şirket seçili
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Vazgeç
          </Button>
          <Button
            onClick={save}
            disabled={saving}
            data-testid="member-companies-save"
            className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
          >
            {saving ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            Kaydet
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ------- Company ↔ Owners assignment mini modal ---------------------
// Reverse of MemberCompaniesDialog: given a company, list every internal
// user who has it in `assigned_company_ids` and let the operator remove
// them (or add more from the "Sahip ekle" select). Each toggle is its own
// PATCH so the caller can bubble up per-user changes without waiting for
// a batch save.
function CompanyOwnersDialog({ open, onOpenChange, company, users, onSaved }) {
  const [pendingId, setPendingId] = useState(null); // user id being patched
  const [addPickId, setAddPickId] = useState("");
  const [localUsers, setLocalUsers] = useState(users || []);

  useEffect(() => {
    setLocalUsers(users || []);
    setAddPickId("");
  }, [users, company?.id]);

  if (!company) return null;

  const owners = localUsers.filter((u) =>
    (u.assigned_company_ids || []).includes(company.id),
  );
  const nonOwners = localUsers.filter(
    (u) => u.active !== false && !(u.assigned_company_ids || []).includes(company.id),
  );

  const patchUser = async (userDoc, nextIds) => {
    setPendingId(userDoc.id);
    try {
      const updated = await api
        .patch(`/users/${userDoc.id}`, { assigned_company_ids: nextIds })
        .then((r) => r.data);
      // Merge locally for immediate UI response, then bubble up.
      setLocalUsers((cur) =>
        cur.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)),
      );
      onSaved?.([updated]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setPendingId(null);
    }
  };

  const removeOwner = (u) => {
    const next = (u.assigned_company_ids || []).filter((x) => x !== company.id);
    return patchUser(u, next);
  };
  const addOwner = () => {
    if (!addPickId) return;
    const u = localUsers.find((x) => x.id === addPickId);
    if (!u) return;
    const next = Array.from(new Set([...(u.assigned_company_ids || []), company.id]));
    patchUser(u, next).then(() => setAddPickId(""));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="company-owners-dialog">
        <DialogHeader>
          <DialogTitle>{company.name} — Sorumlular</DialogTitle>
          <div className="text-xs text-zinc-500">
            Bu şirketten sorumlu kullanıcıları buradan kaldırabilir veya yeni
            sorumlu ekleyebilirsin. Değişiklikler her satırda anlık kaydedilir.
          </div>
        </DialogHeader>

        {/* Sahip ekle */}
        {nonOwners.length > 0 && (
          <div className="mb-2 rounded-lg border border-dashed border-zinc-300 bg-zinc-50/60 p-2.5">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
              Sahip ekle
            </div>
            <div className="flex items-center gap-2">
              <select
                value={addPickId}
                onChange={(e) => setAddPickId(e.target.value)}
                className="h-8 flex-1 rounded-md border border-zinc-200 bg-white px-2 text-xs"
                data-testid="company-owners-add-select"
              >
                <option value="">— Kullanıcı seç —</option>
                {nonOwners.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name} ({u.email})
                  </option>
                ))}
              </select>
              <Button
                size="sm"
                disabled={!addPickId || pendingId !== null}
                onClick={addOwner}
                className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
                data-testid="company-owners-add-btn"
              >
                {pendingId ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <UserPlus className="h-3.5 w-3.5" />
                )}
                Ekle
              </Button>
            </div>
          </div>
        )}

        {/* Sahip listesi — X ile kaldır */}
        <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
          Sorumlular ({owners.length})
        </div>
        {owners.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-200 bg-zinc-50 p-4 text-center text-xs text-zinc-500">
            Bu şirketten sorumlu kullanıcı yok.
          </div>
        ) : (
          <ul className="max-h-[45vh] divide-y divide-zinc-100 overflow-y-auto rounded-lg border border-zinc-200 bg-white">
            {owners.map((u) => {
              const isPending = pendingId === u.id;
              return (
                <li
                  key={u.id}
                  className="group flex items-center gap-3 px-3 py-2.5"
                  data-testid={`company-owner-${u.id}`}
                >
                  <div className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-gradient-to-br from-emerald-500 to-teal-500 text-xs font-semibold text-white">
                    {initials(u.name)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-zinc-900">{u.name}</div>
                    <div className="truncate text-[11px] text-zinc-500">{u.email}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeOwner(u)}
                    disabled={isPending}
                    className="rounded p-1 text-zinc-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 group-hover:opacity-100 disabled:opacity-50"
                    title="Sorumluluğu kaldır"
                    data-testid={`company-owner-remove-${u.id}`}
                  >
                    {isPending ? (
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    ) : (
                      <X className="h-3.5 w-3.5" />
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Kapat
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ------- Small KPI card used on top of the Teams dashboard --------
// Kept inline to avoid a one-file component under `components/`. Tones map
// to Tailwind ramps that already appear elsewhere in the app for visual
// consistency.
function KpiCard({ tone, icon, label, value, sub, testId }) {
  const tones = {
    violet: "from-violet-500 to-fuchsia-500",
    fuchsia: "from-fuchsia-500 to-pink-500",
    sky: "from-sky-500 to-cyan-500",
    emerald: "from-emerald-500 to-teal-500",
    slate: "from-slate-500 to-zinc-500",
  };
  const gradient = tones[tone] || tones.slate;
  return (
    <div
      className="relative overflow-hidden rounded-xl border border-zinc-200 bg-white px-4 py-3 shadow-sm"
      data-testid={testId}
    >
      <div
        className={`pointer-events-none absolute -top-6 -right-6 h-16 w-16 rounded-full bg-gradient-to-br ${gradient} opacity-10`}
      />
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
        <span
          className={`inline-flex h-6 w-6 items-center justify-center rounded-md bg-gradient-to-br ${gradient} text-white`}
        >
          {icon}
        </span>
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tracking-tight text-zinc-950">
        {value}
      </div>
      {sub && (
        <div className="mt-0.5 truncate text-[11px] text-zinc-500">{sub}</div>
      )}
    </div>
  );
}

// ------- Main page -----------------------------------------------
export default function CopTeams() {
  const { user } = useAuthStore();
  const canManage =
    ["super_admin", "ultra_admin"].includes(user?.role) || user?.can_manage_contacts !== false;
  const [teams, setTeams] = useState([]);
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [q, setQ] = useState("");
  const [view, setView] = useState(() => {
    if (typeof window === "undefined") return "grid";
    return window.localStorage.getItem("cop.teams.view") || "grid";
  });

  const load = async () => {
    try {
      const [t1, u1, cs] = await Promise.all([
        teamsApi.list(),
        api.get("/users").then((r) => r.data),
        contactsApi.list({ kind: "company" }),
      ]);
      setTeams(t1);
      setUsers((u1 || []).filter((u) => u.active !== false));
      setCompanies(cs || []);
    } catch (err) {
      toast.error(err.message);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const changeView = (v) => {
    setView(v);
    try {
      window.localStorage.setItem("cop.teams.view", v);
    } catch {
      /* ignore */
    }
  };

  const filtered = useMemo(() => {
    const rx = q.trim().toLowerCase();
    if (!rx) return teams;
    return teams.filter(
      (t) =>
        (t.name || "").toLowerCase().includes(rx) ||
        (t.description || "").toLowerCase().includes(rx),
    );
  }, [teams, q]);

  // -- Dashboard KPIs -------------------------------------------------
  // All aggregate reads on the current in-memory `teams`+`users`+`companies`
  // slice (already tenant-scoped by the API). No extra network round-trip.
  const stats = useMemo(() => {
    const activeUsers = users.filter((u) => u.active !== false);
    // Unique member ids across every team (a user in 2 teams counted once).
    const memberIdSet = new Set();
    teams.forEach((t) => (t.member_ids || []).forEach((id) => memberIdSet.add(id)));
    const memberedUsers = activeUsers.filter((u) => memberIdSet.has(u.id));
    // Team ↔ managed companies (derived from members' assigned_company_ids).
    let biggestTeam = null;
    let biggestTeamSize = -1;
    let hottestTeam = null; // team managing most companies
    let hottestTeamCount = -1;
    let totalAssignments = 0; // sum of every member's assigned_company_ids across teams (double-counted if in >1 team)
    teams.forEach((t) => {
      const size = (t.member_ids || []).length;
      if (size > biggestTeamSize) {
        biggestTeamSize = size;
        biggestTeam = t;
      }
      const mCompanyIds = new Set();
      (t.member_ids || []).forEach((mid) => {
        const u = activeUsers.find((x) => x.id === mid);
        (u?.assigned_company_ids || []).forEach((cid) => mCompanyIds.add(cid));
      });
      totalAssignments += mCompanyIds.size;
      if (mCompanyIds.size > hottestTeamCount) {
        hottestTeamCount = mCompanyIds.size;
        hottestTeam = t;
      }
    });
    // Unique companies assigned to at least one user (whether in a team or not).
    const assignedCompanyIds = new Set();
    activeUsers.forEach((u) =>
      (u.assigned_company_ids || []).forEach((cid) => assignedCompanyIds.add(cid)),
    );
    const orphanCompanyCount = Math.max(0, companies.length - assignedCompanyIds.size);
    // Members without a single company assignment (in any team).
    const unassignedMembers = memberedUsers.filter(
      (u) => (u.assigned_company_ids || []).length === 0,
    );
    const avgSize = teams.length
      ? memberedUsers.length / teams.length
      : 0;
    // Load balance: per-user assignment counts to spot imbalance.
    const perUserCounts = memberedUsers.map((u) => (u.assigned_company_ids || []).length);
    const maxLoad = perUserCounts.length ? Math.max(...perUserCounts) : 0;
    return {
      teamCount: teams.length,
      memberCount: memberedUsers.length,
      companyTotal: companies.length,
      companiesAssigned: assignedCompanyIds.size,
      orphanCompanyCount,
      biggestTeam,
      biggestTeamSize: Math.max(0, biggestTeamSize),
      hottestTeam,
      hottestTeamCount: Math.max(0, hottestTeamCount),
      unassignedMemberCount: unassignedMembers.length,
      avgSize,
      maxLoad,
      totalAssignments,
    };
  }, [teams, users, companies]);

  const performDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      await teamsApi.remove(confirmTarget.id);
      setTeams((cur) => cur.filter((x) => x.id !== confirmTarget.id));
      toast.success("Takım silindi");
      if (selected?.id === confirmTarget.id) setSelected(null);
      setConfirmTarget(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setDeleting(false);
    }
  };

  const onSaved = (doc) => {
    setTeams((cur) => {
      const exists = cur.find((x) => x.id === doc.id);
      return exists ? cur.map((x) => (x.id === doc.id ? doc : x)) : [doc, ...cur];
    });
    if (selected?.id === doc.id) setSelected(doc);
    setEditing(null);
  };

  const openDetail = (tm) => setSelected(tm);

  // Per-team managed company count: union of every member's
  // `assigned_company_ids`. Cached in a Map so the same team hitting both
  // the grid and list views doesn't recompute. Small enough that a plain
  // `for` loop beats useMemo overhead here.
  const managedCountByTeam = (() => {
    const map = new Map();
    teams.forEach((t) => {
      const ids = new Set();
      (t.member_ids || []).forEach((uid) => {
        const u = users.find((x) => x.id === uid);
        (u?.assigned_company_ids || []).forEach((cid) => ids.add(cid));
      });
      map.set(t.id, ids.size);
    });
    return map;
  })();

  return (
    <div className="space-y-6" data-testid="cop-teams-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-zinc-500">
            COP 31
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight text-zinc-950 sm:text-4xl">
            Takımlar
          </h1>
          <p className="mt-1 text-sm text-zinc-500">
            Ekiplerini oluştur, kullanıcıları ata ve tek tıkla üyeleri gör.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-56">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-400" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Takım ara"
              className="pl-7"
              data-testid="team-search"
            />
          </div>
          {/* View toggle */}
          <div className="inline-flex overflow-hidden rounded-md border border-zinc-200 bg-white">
            <button
              type="button"
              onClick={() => changeView("grid")}
              data-testid="team-view-grid"
              title="Kart görünüm"
              className={
                "flex items-center gap-1 px-2 py-1.5 text-xs transition-colors " +
                (view === "grid"
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-600 hover:bg-zinc-50")
              }
            >
              <LayoutGrid className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={() => changeView("list")}
              data-testid="team-view-list"
              title="Liste görünüm"
              className={
                "flex items-center gap-1 px-2 py-1.5 text-xs transition-colors " +
                (view === "list"
                  ? "bg-zinc-900 text-white"
                  : "text-zinc-600 hover:bg-zinc-50")
              }
            >
              <ListIcon className="h-3.5 w-3.5" />
            </button>
          </div>
          {canManage && (
            <Button
              onClick={() => {
                setEditing(null);
                setDialogOpen(true);
              }}
              className="gap-1 bg-zinc-900 text-white hover:bg-zinc-800"
              data-testid="cop-teams-add-btn"
            >
              <Plus className="h-4 w-4" />
              Yeni takım
            </Button>
          )}
        </div>
      </div>

      {/* --- Dashboard KPI şeridi -------------------------------------
          Sayfa üstünde takım+üye+şirket ilişkilerinden türetilmiş 4 birincil
          + 3 ikincil KPI. Boş durumu grid'i tamamen gizler, veri varken
          ekleme/atama kararlarını sayfa değiştirmeden alabilmek için yer alır. */}
      {teams.length > 0 && (
        <div className="space-y-3" data-testid="teams-dashboard">
          {/* Primary metrics — büyük kartlar */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <KpiCard
              tone="violet"
              icon={<Users2 className="h-4 w-4" />}
              label="Toplam Takım"
              value={stats.teamCount}
              sub={`Ort. ${stats.avgSize.toFixed(1)} üye / takım`}
              testId="kpi-teams"
            />
            <KpiCard
              tone="fuchsia"
              icon={<UserPlus className="h-4 w-4" />}
              label="Aktif Üye"
              value={stats.memberCount}
              sub={
                stats.unassignedMemberCount > 0
                  ? `${stats.unassignedMemberCount} kişi henüz şirketsiz`
                  : "Herkes bir şirketten sorumlu"
              }
              testId="kpi-members"
            />
            <KpiCard
              tone="sky"
              icon={<Building2 className="h-4 w-4" />}
              label="Yönetilen Şirket"
              value={stats.companiesAssigned}
              sub={
                stats.companyTotal > 0
                  ? `${stats.companyTotal} şirket · ${stats.orphanCompanyCount} atanmamış`
                  : "Şirket kaydı yok"
              }
              testId="kpi-companies"
            />
            <KpiCard
              tone="emerald"
              icon={<Save className="h-4 w-4" />}
              label="Ortalama Yük"
              value={
                stats.memberCount > 0
                  ? (stats.totalAssignments / Math.max(stats.memberCount, 1)).toFixed(1)
                  : "0"
              }
              sub={
                stats.maxLoad > 0
                  ? `En yüksek yük: ${stats.maxLoad} şirket / kişi`
                  : "Henüz atama yok"
              }
              testId="kpi-avg-load"
            />
          </div>

          {/* Secondary spotlight — en büyük & en hızlı takım + coverage */}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div
              className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3"
              data-testid="kpi-biggest-team"
            >
              <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-violet-50 text-violet-700">
                <Users2 className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  En büyük takım
                </div>
                <div className="truncate text-sm font-medium text-zinc-900">
                  {stats.biggestTeam?.name || "—"}
                </div>
                <div className="text-[11px] text-zinc-500">
                  {stats.biggestTeamSize} üye
                </div>
              </div>
              {stats.biggestTeam && (
                <button
                  type="button"
                  onClick={() => openDetail(stats.biggestTeam)}
                  className="rounded-md border border-zinc-200 px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
                  data-testid="kpi-open-biggest"
                >
                  Aç
                </button>
              )}
            </div>

            <div
              className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3"
              data-testid="kpi-hottest-team"
            >
              <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-sky-50 text-sky-700">
                <Building2 className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  En yoğun portföy
                </div>
                <div className="truncate text-sm font-medium text-zinc-900">
                  {stats.hottestTeam?.name || "—"}
                </div>
                <div className="text-[11px] text-zinc-500">
                  {stats.hottestTeamCount} şirket yönetiyor
                </div>
              </div>
              {stats.hottestTeam && (
                <button
                  type="button"
                  onClick={() => openDetail(stats.hottestTeam)}
                  className="rounded-md border border-zinc-200 px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
                  data-testid="kpi-open-hottest"
                >
                  Aç
                </button>
              )}
            </div>

            <div
              className="flex items-center gap-3 rounded-xl border border-zinc-200 bg-white px-4 py-3"
              data-testid="kpi-coverage"
            >
              <div className="flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-emerald-50 text-emerald-700">
                <Mail className="h-4 w-4" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-500">
                  Şirket kapsama oranı
                </div>
                <div className="text-sm font-medium text-zinc-900">
                  {stats.companyTotal > 0
                    ? `%${Math.round((stats.companiesAssigned / stats.companyTotal) * 100)}`
                    : "—"}
                </div>
                <div className="text-[11px] text-zinc-500">
                  {stats.companiesAssigned}/{stats.companyTotal} şirket bir sorumluya bağlı
                </div>
              </div>
              {/* mini gauge — thin fill bar */}
              <div className="hidden h-1.5 w-24 flex-none overflow-hidden rounded-full bg-zinc-100 sm:block">
                <div
                  className="h-full rounded-full bg-emerald-500 transition-all"
                  style={{
                    width:
                      stats.companyTotal > 0
                        ? `${Math.round((stats.companiesAssigned / stats.companyTotal) * 100)}%`
                        : "0%",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}


      {filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50 p-10 text-center text-sm text-zinc-500">
          <Users2 className="mx-auto mb-2 h-8 w-8 text-zinc-400" />
          {teams.length === 0
            ? "Henüz takım yok. 'Yeni takım' ile başlayabilirsin."
            : "Aramanla eşleşen takım bulunamadı."}
        </div>
      ) : view === "grid" ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((tm) => (
            <button
              key={tm.id}
              type="button"
              onClick={() => openDetail(tm)}
              className="group rounded-xl border border-zinc-200 bg-white p-4 text-left shadow-[0_1px_2px_rgba(0,0,0,0.03)] transition-all hover:-translate-y-0.5 hover:shadow-md"
              data-testid={`team-card-${tm.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <div
                    className="flex h-9 w-9 items-center justify-center rounded-lg text-white shadow"
                    style={{ background: tm.color }}
                  >
                    <Users2 className="h-4 w-4" />
                  </div>
                  <div>
                    <div className="font-semibold text-zinc-900">{tm.name}</div>
                    <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                      <span className="inline-flex items-center gap-1">
                        <Users2 className="h-3 w-3" />
                        {tm.member_ids?.length || 0} üye
                      </span>
                      <span className="text-zinc-300">·</span>
                      <span
                        className="inline-flex items-center gap-1"
                        data-testid={`team-card-companies-${tm.id}`}
                      >
                        <Building2 className="h-3 w-3" />
                        {managedCountByTeam.get(tm.id) || 0} şirket
                      </span>
                    </div>
                  </div>
                </div>
                {canManage && (
                  <div
                    className="flex items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditing(tm);
                        setDialogOpen(true);
                      }}
                      className="cursor-pointer rounded p-1 text-xs text-zinc-500 hover:bg-zinc-100"
                    >
                      Düzenle
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        setConfirmTarget(tm);
                      }}
                      data-testid={`team-delete-icon-${tm.id}`}
                      className="cursor-pointer rounded p-1 text-zinc-500 hover:bg-red-50 hover:text-red-600"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </span>
                  </div>
                )}
              </div>
              {tm.description && (
                <p className="mt-2 line-clamp-2 text-xs text-zinc-600">{tm.description}</p>
              )}
              <div className="mt-3">
                <AvatarStack users={users} memberIds={tm.member_ids || []} />
              </div>
            </button>
          ))}
        </div>
      ) : (
        // List view
        <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.03)]">
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 bg-zinc-50/60 text-[11px] uppercase tracking-wider text-zinc-500">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Takım</th>
                <th className="px-4 py-3 text-left font-semibold">Açıklama</th>
                <th className="px-4 py-3 text-left font-semibold">Üyeler</th>
                <th className="px-4 py-3 text-left font-semibold"># Üye</th>
                <th className="px-4 py-3 text-left font-semibold"># Şirket</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {filtered.map((tm) => (
                <tr
                  key={tm.id}
                  onClick={() => openDetail(tm)}
                  className="cursor-pointer transition-colors hover:bg-zinc-50"
                  data-testid={`team-row-${tm.id}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-white shadow"
                        style={{ background: tm.color }}
                      >
                        <Users2 className="h-4 w-4" />
                      </div>
                      <span className="font-medium text-zinc-900">{tm.name}</span>
                    </div>
                  </td>
                  <td className="max-w-md truncate px-4 py-3 text-xs text-zinc-600">
                    {tm.description || "—"}
                  </td>
                  <td className="px-4 py-3">
                    <AvatarStack users={users} memberIds={tm.member_ids || []} />
                  </td>
                  <td className="px-4 py-3 text-zinc-700">
                    {tm.member_ids?.length || 0}
                  </td>
                  <td className="px-4 py-3" data-testid={`team-row-companies-${tm.id}`}>
                    <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-800">
                      <Building2 className="h-3 w-3" />
                      {managedCountByTeam.get(tm.id) || 0}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {canManage && (
                      <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          onClick={() => {
                            setEditing(tm);
                            setDialogOpen(true);
                          }}
                          className="rounded p-1 text-xs text-zinc-500 hover:bg-zinc-100"
                        >
                          Düzenle
                        </button>
                        <button
                          type="button"
                          onClick={() => setConfirmTarget(tm)}
                          data-testid={`team-delete-icon-${tm.id}`}
                          className="rounded p-1 text-zinc-500 hover:bg-red-50 hover:text-red-600"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <TeamDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) setEditing(null);
        }}
        users={users}
        initial={editing}
        onSaved={onSaved}
      />

      <TeamDetailDrawer
        open={!!selected}
        onOpenChange={(v) => !v && setSelected(null)}
        team={selected}
        users={users}
        companies={companies}
        canManage={canManage}
        onEdit={(tm) => {
          setEditing(tm);
          setDialogOpen(true);
        }}
        onDelete={(tm) => setConfirmTarget(tm)}
        onMembersChanged={(doc) => {
          setSelected(doc);
          setTeams((cur) => cur.map((x) => (x.id === doc.id ? doc : x)));
        }}
        onUserChanged={(updatedUser) => {
          // Optimistically merge the updated user (with fresh
          // assigned_company_ids) into the shared users list so the drawer
          // re-derives its "Yönetilen Şirketler" list on the next render.
          setUsers((cur) =>
            cur.map((u) => (u.id === updatedUser.id ? { ...u, ...updatedUser } : u)),
          );
        }}
      />

      <AlertDialog
        open={!!confirmTarget}
        onOpenChange={(v) => !v && setConfirmTarget(null)}
      >
        <AlertDialogContent data-testid="team-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Takımı sil?</AlertDialogTitle>
            <AlertDialogDescription>
              <span className="font-medium text-zinc-900">&ldquo;{confirmTarget?.name}&rdquo;</span>{" "}
              takımı kalıcı olarak silinecek. Üyeler bilgilerini kaybetmez ama takım
              bağlantısı kaybolur. Emin misin?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="team-delete-cancel">
              Vazgeç
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={performDelete}
              disabled={deleting}
              data-testid="team-delete-confirm-yes"
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {deleting ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="mr-1 h-4 w-4" />
              )}
              Evet, sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
